import { Any } from "cosmjs-types/google/protobuf/any.js";
import { MsgSend } from "cosmjs-types/cosmos/bank/v1beta1/tx.js";
import {
  QueryBalanceRequest,
  QueryBalanceResponse,
} from "cosmjs-types/cosmos/bank/v1beta1/query.js";
import {
  QueryAccountRequest,
  QueryAccountResponse,
} from "cosmjs-types/cosmos/auth/v1beta1/query.js";
import { BaseAccount } from "cosmjs-types/cosmos/auth/v1beta1/auth.js";
import {
  AuthInfo,
  Fee,
  ModeInfo,
  SignDoc,
  SignerInfo,
  TxBody,
  TxRaw,
} from "cosmjs-types/cosmos/tx/v1beta1/tx.js";
import { SignMode } from "cosmjs-types/cosmos/tx/signing/v1beta1/signing.js";
import { CHAIN, FEE_BASE } from "./config.mjs";
import { parseBaseAccount } from "./core.mjs";

function base64(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value) {
  const binary = atob(value);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

function hex(bytes) {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("").toUpperCase();
}

function hexToBase64(value) {
  if (!/^[0-9a-f]{64}$/i.test(value)) throw new Error("Malformed transaction hash");
  return base64(Uint8Array.from(value.match(/.{2}/g), (byte) => Number.parseInt(byte, 16)));
}

export function websocketRpc(method, params, {
  WebSocketImpl = globalThis.WebSocket,
  timeoutMs = 10_000,
} = {}) {
  return new Promise((resolve, reject) => {
    if (!WebSocketImpl) {
      reject(new Error("WebSocket is unavailable"));
      return;
    }
    const socket = new WebSocketImpl(CHAIN.websocket);
    const timer = setTimeout(() => {
      socket.close();
      reject(new Error(`WebSocket ${method} timed out`));
    }, timeoutMs);
    const finish = (callback, value) => {
      clearTimeout(timer);
      socket.close();
      callback(value);
    };
    socket.addEventListener("open", () => socket.send(JSON.stringify({
      jsonrpc: "2.0",
      id: 1,
      method,
      params,
    })));
    socket.addEventListener("message", (event) => {
      const payload = JSON.parse(String(event.data));
      if (payload.error) {
        const error = new Error(payload.error.data || payload.error.message);
        error.rpcCode = payload.error.code;
        finish(reject, error);
        return;
      }
      finish(resolve, payload.result);
    });
    socket.addEventListener("error", () => {
      finish(reject, new Error(`WebSocket ${method} failed`));
    });
  });
}

async function abciQuery(path, requestBytes, rpcRequest = websocketRpc) {
  const result = await rpcRequest("abci_query", {
    path,
    data: hex(requestBytes),
    height: "0",
    prove: false,
  });
  const response = result?.response;
  if (Number(response?.code) !== 0) {
    throw new Error(response?.log || `ABCI query failed with code ${response?.code}`);
  }
  return fromBase64(response.value || "");
}

function readVarint(bytes, offset) {
  let value = 0;
  let shift = 0;
  let index = offset;
  while (index < bytes.length) {
    const byte = bytes[index++];
    value += (byte & 0x7f) * (2 ** shift);
    if ((byte & 0x80) === 0) return { value, next: index };
    shift += 7;
  }
  throw new Error("Malformed protobuf length");
}

export function decodeBaseAccountAny(accountAny) {
  if (!accountAny?.value?.length) throw new Error("Malformed account response");
  if (accountAny.typeUrl === "/cosmos.auth.v1beta1.BaseAccount") {
    return BaseAccount.decode(accountAny.value);
  }
  // Cosmos-EVM account wrappers place BaseAccount in protobuf field 1.
  if (accountAny.value[0] !== 0x0a) throw new Error("Unsupported account type");
  const length = readVarint(accountAny.value, 1);
  return BaseAccount.decode(accountAny.value.slice(length.next, length.next + length.value));
}

function encodeEthPubkey(key) {
  if (key.length >= 128) throw new Error("Unexpected public-key length");
  return Uint8Array.from([0x0a, key.length, ...key]);
}

export function makeIpiSignDoc({
  fromAddress,
  toAddress,
  amount,
  pubkey,
  accountNumber,
  sequence,
}) {
  const msg = MsgSend.fromPartial({
    fromAddress,
    toAddress,
    amount: [{ denom: CHAIN.baseDenom, amount }],
  });
  const bodyBytes = TxBody.encode(TxBody.fromPartial({
    messages: [Any.fromPartial({
      typeUrl: "/cosmos.bank.v1beta1.MsgSend",
      value: MsgSend.encode(msg).finish(),
    })],
    memo: "",
  })).finish();
  const publicKey = Any.fromPartial({
    typeUrl: CHAIN.pubkeyTypeUrl,
    value: encodeEthPubkey(pubkey),
  });
  const authInfoBytes = AuthInfo.encode(AuthInfo.fromPartial({
    signerInfos: [SignerInfo.fromPartial({
      publicKey,
      modeInfo: ModeInfo.fromPartial({
        single: { mode: SignMode.SIGN_MODE_DIRECT },
      }),
      sequence,
    })],
    fee: Fee.fromPartial({
      amount: [{ denom: CHAIN.baseDenom, amount: FEE_BASE }],
      gasLimit: BigInt(CHAIN.gasLimit),
    }),
  })).finish();
  return SignDoc.fromPartial({
    bodyBytes,
    authInfoBytes,
    chainId: CHAIN.chainId,
    accountNumber,
  });
}

function sameBytes(left, right) {
  return left.length === right.length && left.every((byte, index) => byte === right[index]);
}

export function assertUnchangedSignDoc(expected, signed) {
  if (
    signed.chainId !== expected.chainId
    || BigInt(signed.accountNumber) !== expected.accountNumber
    || !sameBytes(signed.bodyBytes, expected.bodyBytes)
    || !sameBytes(signed.authInfoBytes, expected.authInfoBytes)
  ) {
    throw new Error("Keplr changed essential SignDoc fields; refusing broadcast");
  }
}

export async function queryAccount(fetchFn, address, rpcRequest = websocketRpc) {
  try {
    const response = await fetchFn(
      `${CHAIN.rest}/cosmos/auth/v1beta1/accounts/${encodeURIComponent(address)}`,
    );
    if (!response.ok) throw new Error(`Account query failed (HTTP ${response.status})`);
    return parseBaseAccount(await response.json());
  } catch (error) {
    if (!(error instanceof TypeError)) throw error;
    const request = QueryAccountRequest.encode({ address }).finish();
    const response = QueryAccountResponse.decode(await abciQuery(
      "/cosmos.auth.v1beta1.Query/Account",
      request,
      rpcRequest,
    ));
    const account = decodeBaseAccountAny(response.account);
    return {
      accountNumber: account.accountNumber,
      sequence: account.sequence,
    };
  }
}

export async function queryBalance(fetchFn, address, rpcRequest = websocketRpc) {
  try {
    const response = await fetchFn(
      `${CHAIN.rest}/cosmos/bank/v1beta1/balances/${encodeURIComponent(address)}/by_denom`
        + `?denom=${CHAIN.baseDenom}`,
    );
    if (response.status === 404) return "0";
    if (!response.ok) throw new Error(`Balance query failed (HTTP ${response.status})`);
    const payload = await response.json();
    const amount = payload?.balance?.amount ?? "0";
    if (!/^\d+$/.test(amount)) throw new Error("Malformed balance response");
    return amount;
  } catch (error) {
    if (!(error instanceof TypeError)) throw error;
    const request = QueryBalanceRequest.encode({
      address,
      denom: CHAIN.baseDenom,
    }).finish();
    const response = QueryBalanceResponse.decode(await abciQuery(
      "/cosmos.bank.v1beta1.Query/Balance",
      request,
      rpcRequest,
    ));
    return response.balance?.amount || "0";
  }
}

export async function signAndBroadcast({ keplr, fetchFn, address, signDoc }) {
  const response = await keplr.signDirect(
    CHAIN.chainId,
    address,
    signDoc,
    { preferNoSetFee: true, preferNoSetMemo: true },
  );
  assertUnchangedSignDoc(signDoc, response.signed);
  const txBytes = TxRaw.encode(TxRaw.fromPartial({
    bodyBytes: response.signed.bodyBytes,
    authInfoBytes: response.signed.authInfoBytes,
    signatures: [fromBase64(response.signature.signature)],
  })).finish();
  try {
    const broadcast = await fetchFn(`${CHAIN.rest}/cosmos/tx/v1beta1/txs`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        tx_bytes: base64(txBytes),
        mode: "BROADCAST_MODE_SYNC",
      }),
    });
    if (!broadcast.ok) throw new Error(`Broadcast failed (HTTP ${broadcast.status})`);
    const payload = await broadcast.json();
    const result = payload?.tx_response;
    if (!result?.txhash) throw new Error("Broadcast returned no transaction hash");
    if (Number(result.code) !== 0) {
      throw new Error(`Broadcast rejected transaction (code ${result.code}): ${result.raw_log}`);
    }
    return String(result.txhash).toUpperCase();
  } catch (error) {
    if (!(error instanceof TypeError)) throw error;
    if (typeof keplr.sendTx !== "function") throw new Error("Keplr sendTx is unavailable");
    return hex(await keplr.sendTx(CHAIN.chainId, txBytes, "sync"));
  }
}

export async function waitForInclusion(fetchFn, txHash, {
  timeoutMs = 60_000,
  pollMs = 1_500,
  now = Date.now,
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  rpcRequest = websocketRpc,
} = {}) {
  const deadline = now() + timeoutMs;
  while (now() < deadline) {
    try {
      const response = await fetchFn(`${CHAIN.rest}/cosmos/tx/v1beta1/txs/${txHash}`);
      if (response.ok) return (await response.json()).tx_response;
      if (response.status !== 404) {
        throw new Error(`Transaction lookup failed (HTTP ${response.status})`);
      }
    } catch (error) {
      if (!(error instanceof TypeError)) throw error;
      try {
        const result = await rpcRequest("tx", { hash: hexToBase64(txHash), prove: false });
        return {
          txhash: result.hash,
          height: result.height,
          code: Number(result.tx_result?.code || 0),
          raw_log: result.tx_result?.log || "",
        };
      } catch (rpcError) {
        if (!/not found/i.test(rpcError.message)) throw rpcError;
      }
    }
    await sleep(pollMs);
  }
  throw new Error("Timed out waiting for transaction inclusion");
}

export function signDocFixtureBytes(signDoc) {
  return SignDoc.encode(signDoc).finish();
}
